import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Bell, FileText, MessageSquare, ArrowRightCircle, CheckCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = {
  status_change: ArrowRightCircle,
  document_uploaded: FileText,
  new_note: MessageSquare,
  invoice: FileText,
  custom: Bell,
};

// Notification sound using Web Audio API
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
    // Second tone
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.frequency.value = 1100;
    osc2.type = 'sine';
    gain2.gain.setValueAtTime(0.2, ctx.currentTime + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    osc2.start(ctx.currentTime + 0.15);
    osc2.stop(ctx.currentTime + 0.6);
  } catch {}
}

export default function NotificationBell({ testIdSuffix = '' }) {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const prevUnreadRef = useRef(0);
  const ref = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      const newUnread = res.data.unread_count || 0;
      // Play sound if unread count increased
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        playNotificationSound();
      }
      prevUnreadRef.current = newUnread;
      setNotifications(res.data.notifications || []);
      setUnreadCount(newUnread);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markRead = async (notifId) => {
    try {
      await axios.post(`${API}/notifications/${notifId}/read`, {}, { withCredentials: true });
      fetchNotifications();
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      fetchNotifications();
    } catch {}
  };

  const handleClick = (notif) => {
    if (!notif.is_read) markRead(notif.notif_id);
    setOpen(false);
    // Navigate to case detail if case_id present
    if (notif.case_id) {
      const role = user?.role || 'client';
      const basePath = role === 'admin' ? '/admin' : role === 'lawyer' ? '/lawyer' : '/client';
      navigate(`${basePath}/cases/${notif.case_id}`);
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return lang === 'ar' ? 'الآن' : 'Just now';
    if (mins < 60) return lang === 'ar' ? `منذ ${mins} د` : `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return lang === 'ar' ? `منذ ${hrs} س` : `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return lang === 'ar' ? `منذ ${days} ي` : `${days}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid={`notification-bell${testIdSuffix}`}
      >
        <Bell className="w-5 h-5 text-[#475569]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -end-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute end-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-[#0F172A] font-heading">
              {lang === 'ar' ? 'الإشعارات' : 'Notifications'}
            </h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#B48E4B] hover:underline flex items-center gap-1" data-testid="mark-all-read">
                <CheckCheck className="w-3 h-3" /> {lang === 'ar' ? 'قراءة الكل' : 'Mark all read'}
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#94A3B8]">
                {lang === 'ar' ? 'لا توجد إشعارات' : 'No notifications'}
              </div>
            ) : (
              notifications.map(n => {
                const Icon = typeIcons[n.type] || Bell;
                return (
                  <button
                    key={n.notif_id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-start px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3 ${!n.is_read ? 'bg-[#B48E4B]/5' : ''}`}
                    data-testid={`notification-${n.notif_id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${!n.is_read ? 'bg-[#B48E4B]/10 text-[#B48E4B]' : 'bg-slate-100 text-[#94A3B8]'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium truncate ${!n.is_read ? 'text-[#0F172A]' : 'text-[#475569]'}`}>{n.title}</p>
                      <p className="text-xs text-[#94A3B8] line-clamp-2 mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-[#94A3B8] mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.is_read && <div className="w-2 h-2 bg-[#B48E4B] rounded-full flex-shrink-0 mt-2" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
